/**
 * Global help-docs command palette (Cmd+K) — R-131-SUP-C
 */
// R-131-SUP-C
import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { HelpSearchHit, HelpSlug } from '../../lib/helpArticles';
import { helpArticlePath, searchHelpArticles } from '../../lib/helpArticles';

interface Props {
  open: boolean;
  onClose: () => void;
  /** When set, selecting a hit opens the drawer instead of navigating. */
  onSelectSlug?: (slug: HelpSlug) => void;
}

export function HelpDocsCommandPalette({ open, onClose, onSelectSlug }: Props): React.ReactElement | null {
  const navigate = useNavigate();
  const [query, setQuery] = React.useState('');
  const [highlightIndex, setHighlightIndex] = React.useState(0);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const hits = React.useMemo(() => searchHelpArticles(query), [query]);

  React.useEffect(() => {
    if (open) {
      setQuery('');
      setHighlightIndex(0);
      const handle = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(handle);
    }
    return undefined;
  }, [open]);

  React.useEffect(() => {
    if (hits.length === 0) {
      setHighlightIndex(0);
      return;
    }
    setHighlightIndex((prev) => {
      if (!Number.isFinite(prev) || prev < 0) return 0;
      if (prev >= hits.length) return hits.length - 1;
      return prev;
    });
  }, [hits.length]);

  const runHit = React.useCallback(
    (hit: HelpSearchHit | undefined) => {
      if (!hit) return;
      if (onSelectSlug) {
        onSelectSlug(hit.slug);
      } else {
        navigate(helpArticlePath(hit.slug));
      }
      onClose();
    },
    [navigate, onClose, onSelectSlug],
  );

  const onKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightIndex((prev) => (hits.length === 0 ? 0 : Math.min(prev + 1, hits.length - 1)));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightIndex((prev) => Math.max(0, prev - 1));
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        runHit(hits[highlightIndex]);
      }
    },
    [highlightIndex, hits, onClose, runHit],
  );

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] bg-canvas/40 flex items-start justify-center pt-24 p-4"
      data-testid="help-docs-palette"
      role="dialog"
      aria-modal="true"
      aria-label="Search docs"
      onKeyDown={onKeyDown}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="bg-surface-1 rounded-lg shadow-xl w-full max-w-lg overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-border">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search docs…"
            className="w-full px-4 py-3 text-sm bg-transparent focus:outline-none placeholder:text-text-muted"
            data-testid="help-docs-palette-input"
            aria-label="Search help articles"
            autoComplete="off"
            spellCheck={false}
          />
        </div>
        <ul className="max-h-80 overflow-y-auto" data-testid="help-docs-palette-list" role="listbox">
          {hits.length === 0 ? (
            <li className="px-4 py-6 text-xs text-text-muted text-center" data-testid="help-docs-palette-empty">
              No docs match &quot;{query}&quot;
            </li>
          ) : (
            hits.map((hit, idx) => {
              const isActive = idx === highlightIndex;
              return (
                <li
                  key={`${hit.slug}-${hit.matchKind}-${hit.matchLabel}`}
                  role="option"
                  aria-selected={isActive}
                  className={[
                    'px-4 py-2 text-sm cursor-pointer',
                    isActive ? 'bg-surface-3' : 'hover:bg-surface-2',
                  ].join(' ')}
                  data-testid={`help-docs-palette-item-${hit.slug}-${idx}`}
                  data-active={isActive ? 'true' : 'false'}
                  onMouseEnter={() => setHighlightIndex(idx)}
                  onClick={() => runHit(hit)}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="truncate text-text-primary">{hit.matchLabel}</span>
                    <span className="text-[10px] text-text-muted shrink-0 uppercase tracking-wider">
                      {hit.matchKind === 'heading' ? '§' : 'doc'}
                    </span>
                  </div>
                  <div className="text-[10px] text-text-muted truncate mt-0.5">
                    {hit.title} · {hit.section}
                  </div>
                </li>
              );
            })
          )}
        </ul>
        <div className="border-t border-border-subtle px-4 py-2 text-[10px] text-text-muted flex items-center justify-between">
          <span>
            <kbd className="font-mono">↑↓</kbd> navigate · <kbd className="font-mono">↵</kbd> open ·{' '}
            <kbd className="font-mono">esc</kbd> close
          </span>
          <span>{hits.length} {hits.length === 1 ? 'result' : 'results'}</span>
        </div>
      </div>
    </div>
  );
}
